import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Alan, Buton, Iskelet, Kart } from "@koclukpanel/ui";
import {
  ApiHatasi,
  anonimlestirmeModu,
  anonimlestirmeOnizleme,
  type AnonimlestirmeModu,
  type AnonimlestirmeOzeti,
} from "./api";
import { useSudoKapisi } from "./SudoKapisi";

/**
 * SAKLAMA SÜRESİ — kişisel veri anonimleştirme (F-LGL-06, Emre 06.10.2026).
 *
 * Ayrılan öğrenci 2 yıl, kaybedilen aday 6 ay sonra anonimleşir. Ekran
 * yalnız SAYI gösterir (KURU önizleme) — hiçbir kişi listelenmez.
 *
 * AÇIK geri alınamaz: düğme onay metnini yazdırmadan çalışmaz (sunucu da
 * aynı metni istiyor; ekrandaki kontrol yalnız kolaylık).
 */

const ONAY_METNI = "ANONIMLESTIRMEYI AC";

const MOD_ADI: Record<AnonimlestirmeModu, string> = {
  KAPALI: "Kapalı — hiçbir şey yapılmaz",
  KURU: "Kuru — her gece yalnız sayılır, hiçbir şey değişmez",
  ACIK: "Açık — her gece anonimleştirilir (geri alınamaz)",
};

function Sayilar({ o }: { o: AnonimlestirmeOzeti }) {
  const satirlar: Array<[string, number]> = [
    ["Öğrenci", o.ogrenci],
    ["— hesabı korunan (aynı zamanda aktif veli)", o.ogrenciKullanicisiKorunan],
    ["Veli", o.veli],
    ["Ödeyen veli (ad ve T.C. no finans için kalır)", o.odeyenVeli],
    ["Aday", o.aday],
    ["Dosya", o.dosya],
    ["— bunlardan rıza kanıtı (ıslak imzalı onay taraması)", o.rizaKaniti],
    ["Rapor PDF'i", o.pdf],
    ["Ertelenen — açık KVKK başvurusu", o.ertelenen.kvkkBasvurusu],
    ["Ertelenen — ödenmemiş taksit", o.ertelenen.odemeAcik],
    ["Ertelenen — süren iade", o.ertelenen.iadeSuruyor],
    ["Tarihsiz öğrenci (ayrılış tarihi yok)", o.tarihsiz.ogrenci],
    ["Tarihsiz aday", o.tarihsiz.aday],
    ["Durumu değiştiği için işlenmeyen", o.uygunlukDegisti],
    ["Önceki gecelerden silinmeyi bekleyen dosya", o.oncekiBekleyenDosya],
    ["— rapor PDF klasörü (öğrenci)", o.oncekiBekleyenOnek],
    ["— yarım kalmış yükleme", o.oncekiBekleyenNiyet],
    ["PDF sayısı okunamayan öğrenci (bilinmiyor)", o.pdfBilinmeyen],
    ["Silinemeyen dosya (sonraki gece yeniden denenir)", o.bekleyenNesne],
    ["Yeniden süpürülen öğrenci (anonimleştirmeden sonraki geceler)", o.supurulen],
  ];
  return (
    <>
      {o.butceKesilen > 0 && (
        <p role="status" className="text-notr-900 mb-2 text-sm">
          Süre bütçesi doldu: bu sayılar bütün envanter değil (eksik). Kalan kayıtlar sonraki gece
          işlenir.
        </p>
      )}
      <dl className="grid grid-cols-[1fr_auto] gap-x-4 gap-y-1 text-sm">
        {satirlar.map(([ad, n]) => (
          <div key={ad} className="contents">
            <dt className="text-notr-700">{ad}</dt>
            <dd className="text-notr-900 text-right font-medium tabular-nums">{n}</dd>
          </div>
        ))}
      </dl>
    </>
  );
}

export function KvkkSaklama() {
  const qc = useQueryClient();
  const [onay, setOnay] = useState("");
  const { hataYakala, kapi, sorguKapisi } = useSudoKapisi(
    "Anonimleştirme ayarı kalıcı silme yetkisi ister. Devam etmeden önce şifrenizi doğrulayın.",
  );

  const durum = useQuery({
    queryKey: ["kvkk-anonimlestirme"],
    queryFn: anonimlestirmeOnizleme,
    retry: false,
  });

  const degistir = useMutation({
    mutationFn: (m: AnonimlestirmeModu) => anonimlestirmeModu(m, m === "ACIK" ? onay : null),
    onSuccess: async () => {
      setOnay("");
      await qc.invalidateQueries({ queryKey: ["kvkk-anonimlestirme"] });
    },
    // Sudo penceresi açılınca AYNI mod yeniden denenir.
    onError: (e, m) => hataYakala(() => degistir.mutate(m))(e),
  });

  if (durum.isError) {
    const kapiEkrani = sorguKapisi(durum.error, () => void durum.refetch());
    if (kapiEkrani) return <>{kapiEkrani}</>;
  }
  if (durum.isPending) return <Iskelet satir={4} />;
  if (durum.isError) {
    return (
      <p role="alert" className="text-hata-500 text-sm">
        {durum.error instanceof ApiHatasi ? durum.error.message : "Ayar okunamadı."}
      </p>
    );
  }

  const { mod, ozet } = durum.data;
  return (
    <div className="flex flex-col gap-4">
      {kapi}
      <Kart baslik="Saklama süresi dolan kişisel veri">
        <p className="text-notr-700 text-sm">
          Ayrılan öğrencinin kişisel verisi ayrılıştan <strong>2 yıl</strong>, kaybedilen adayın
          verisi <strong>6 ay</strong> sonra anonimleştirilir. Kayıtlar silinmez; ad, iletişim,
          notlar ve dosyalar kaldırılır, istatistik ve finans kayıtları kalır.
        </p>
        <p className="text-notr-900 mt-3 text-sm">
          Şu anki mod: <strong>{MOD_ADI[mod]}</strong>
        </p>
      </Kart>

      <Kart baslik="Bu gece açık olsaydı (önizleme — hiçbir şey değişmedi)">
        {/* F-LGL-06 koordinatör kararı (06.10.2026): önizleme bir TAHMİN, veri işlemi değil. */}
        <p className="text-notr-700 mb-2 text-sm">
          Önizleme bir tahmindir: sıradaki açık koşuyla aynı yerden başlar, ama süre sınırı yüzünden
          açık koşu farklı sayıda kayıt işleyebilir. Süre yetmediyse önizleme “eksik” olarak
          işaretlenir.
        </p>
        <Sayilar o={ozet} />
      </Kart>

      <Kart baslik="Modu değiştir">
        <div className="flex flex-col gap-3">
          <div className="flex flex-wrap gap-2">
            <Buton
              gorunum="ikincil"
              disabled={mod === "KAPALI" || degistir.isPending}
              onClick={() => degistir.mutate("KAPALI")}
            >
              Kapat
            </Buton>
            <Buton
              gorunum="ikincil"
              disabled={mod === "KURU" || degistir.isPending}
              onClick={() => degistir.mutate("KURU")}
            >
              Kuru moda al
            </Buton>
          </div>
          <Alan
            etiket={`Açmak için "${ONAY_METNI}" yazın`}
            value={onay}
            onChange={(e) => setOnay(e.target.value)}
            autoComplete="off"
            yardim="Açık modda her gece anonimleştirme yapılır ve geri alınamaz."
          />
          <div>
            <Buton
              gorunum="tehlike"
              disabled={mod === "ACIK" || onay !== ONAY_METNI || degistir.isPending}
              onClick={() => degistir.mutate("ACIK")}
            >
              Anonimleştirmeyi aç
            </Buton>
          </div>
          {degistir.isError &&
            !(degistir.error instanceof ApiHatasi && degistir.error.sudoGerekliMi) && (
              <p role="alert" className="text-hata-500 text-sm">
                {degistir.error instanceof ApiHatasi
                  ? degistir.error.message
                  : "Mod değiştirilemedi."}
              </p>
            )}
        </div>
      </Kart>
    </div>
  );
}
